<template>
  <div class="kpt-api-root kpt-single-page">
    <vue-headful title="Portal API" />
    <header class="kpt-single-header">
      <h4>Portal API</h4>
      <div class="page-control">
        <!-- <font-awesome-icon :icon="['far', 'thumbtack']" @click="pinToDash" title="Pin to Dashboard" class="has-cursor-pointer" /> -->
      </div>
    </header>
    <div class="kpt-api-content">
      <nav class="kpt-api-listing">
        <div>
          <header class="sub-header">
            <h6 class="is-size-6 has-text-weight-semibold">Documentation</h6>
          </header>
          <ul>
            <li>
              <a href="#gettingstarted">Getting Started</a>
            </li>
            <li>
              <a href="#authentication">Authentication</a>
            </li>
            <li>
              <a href="#errors">Errors</a>
            </li>
          </ul>
        </div>
        <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
        <div v-for="(ctrl, idx) in controllers" v-if="docsJson" :key="idx">
          <header class="sub-header">
            <h6 class="is-size-6 has-text-weight-semibold">{{ctrl}}</h6>
          </header>
          <ul>
            <li class="truncate" v-for="(path, i) in paths.filter(p => p.cat == ctrl)" :key="i" :title="`${path.path} (${path.verb})`" :class="{ 'is-active': isActiveLink(`#${path.shortcode}`)}">
              <!-- <a :href="`#${path.shortcode}`">{{path.path}}</a> <span class="is-size-7 is-uppercase" :class="getColorFromVerb(path.verb)">{{path.verb}}</span> -->
              <router-link :to="{ path: `#${path.shortcode}` }">{{path.path}} <span class="is-size-7 is-uppercase" :class="getColorFromVerb(path.verb)">{{path.verb}}</span></router-link>
            </li>
          </ul>
        </div>
      </nav>
      <div class="kpt-api-articles">
        <article class="kpt-api-endpoint" id="gettingstarted" ref="gettingstarted">
          <main class="kpt-api-request content">
            <h3 class="title">Getting Started</h3>
            <h5 class="subtitle">Welcome to Kymeta's Portal API</h5>
            <p>This document describes the resources that make up the official Kymeta REST API v2. If you have any problems or requests, please contact Kymeta Support.</p>
            <p>All API requests should be made against <code>{{appConfig.apiUrl}}</code> followed by the endpoint path as listed.</p>
          </main>
          <aside class="kpt-api-response">
          </aside>
        </article>
        <article class="kpt-api-endpoint" id="authentication" ref="authentication">
          <main class="kpt-api-request content">
            <h3 class="title">Authentication</h3>
            <p>All requests to the API require authentication. Unauthorized requests will return <code>404 Not Found</code> in some places to prevent accidental leakage of private information.</p>
            <p>To access the Cloud Services Partner API, please create a client ID and secret (Profile -> API Clients) to get a OAuth2 bearer token from Kymeta's authentication service. This bearer token is passed as a header to the Cloud Services Partners API. The authentication service can be found at <code>{{appConfig.authDomain}}/connect/token</code>. For a sample request, see on the right.</p>
            <b-message type="is-info">
              You must include the Bearer token in the Authentication header with every request.
            </b-message>
          </main>
          <aside class="kpt-api-response">
            <b-tabs>
              <b-tab-item label="Curl">
                <pre class="code" style="white-space: normal"><code>curl 
                  -d "scope=partners-api&grant_type=client_credentials&client_id=6fa3cd9b-8c0c-46b7-a272-8cdde4ae3f73&client_secret=17fb4f932c239c442ab01afce115d713517a08c89faef21f4badbfe0c986008513" 
                  -H "Content-Type: application/x-www-form-urlencoded" 
                  -X POST {{appConfig.authDomain}}
                  </code>
                </pre>
              </b-tab-item>
            </b-tabs>
          </aside>
        </article>
        <article class="kpt-api-endpoint" id="errors" ref="errors">
          <main class="kpt-api-request">
            <h3 class="title">Errors</h3>
            <h5 class="subtitle">Types of errors in a table</h5>
            <table class="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>400</td>
                  <td>Bad Request</td>
                  <td>There was a problem with the user provided input</td>
                </tr>
                <tr>
                  <td>401</td>
                  <td>Unauthorized</td>
                  <td>User does not have access to this endpoint, or the Bearer token was not included in the request or was expired.</td>
                </tr>
                <tr>
                  <td>403</td>
                  <td>Forbidden</td>
                  <td>...</td>
                </tr>
                <tr>
                  <td>404</td>
                  <td>Not Found</td>
                  <td>The URL you're trying to reach does not exist</td>
                </tr>
                <tr>
                  <td>500</td>
                  <td>Internal Server Error</td>
                  <td>There was a problem with the server code.</td>
                </tr>
              </tbody>
            </table>
          </main>
          <aside class="kpt-api-response">
            
          </aside>
        </article>
        <article class="kpt-api-endpoint" v-for="(path, i) in paths" :key="i" :id="path.shortcode" :ref="path.shortcode">
          <main class="kpt-api-request">
            <h3 class="title">{{path.path}} <b-tag class="is-uppercase" :type="getColorFromVerb(path.verb, 'tag')">{{path.verb}}</b-tag></h3>
            <h5 class="subtitle">{{path.summary}}</h5>
            <b-message type="is-info">
              <div class="content" v-html="path.remarks?.replace(/\n/g, '<br />')"></div>
            </b-message>
            <div v-if="path.parameters.length">
              <label class="label">Request Parameters</label>
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Path/Query</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="param in path.parameters" :key="param.name">
                    <td>{{param.name}}</td>
                    <td>{{param.type}} <span v-if="param.format">({{param.format}})</span></td>
                    <td class="has-text-centered">{{param.required ? 'Yes' : 'No'}}</td>
                    <td class="has-text-centered is-capitalized">{{param.in}}</td>
                  </tr>
                  <tr v-if="!path.parameters.length">
                    <td colspan="3">No parameters.</td>
                  </tr>
                </tbody>
              </table>
              <br>
            </div>
            <try-it-out :path="path.path" :verb="path.verb" :parameters="path.parameters"></try-it-out>
          </main>
          <aside class="kpt-api-models">
            <b-tabs type="is-toggle">
              <b-tab-item label="Request" v-if="path.model">
                <main>
                  <p>{{path.model.description}}</p>
                  <div class="kpt-api-viewmodel" v-for="(property, idx) in buildModelPropertyArray(path.model)" :key="idx">
                    <h6><strong>{{property.name}}</strong> <span v-if="property.type">({{property.type}})</span> <span v-if="property.required" class="has-text-danger">(required)</span> <span v-if="property.nullable" class="has-text-info">(nullable)</span></h6>
                    <p>{{property.description}}</p>
                    <ul>
                      <li v-for="(rest, idx) in property.restrictions" :key="idx"><i>{{Object.keys(rest)[0]}}: {{Object.values(rest)[0]}}</i></li>
                    </ul>
                  </div>
                </main>
              </b-tab-item>
              <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
              <b-tab-item label="Response" v-for="(resp, i) in path.responses" :key="i" v-if="isSuccessResponse(resp.statusCode)">
                <main>
                  <p>{{resp.description}}</p>
                  <div v-if="resp.schema">
                    <label class="label">{{resp.statusCode}} ({{resp.contentType ? resp.contentType : ''}})</label>
                    <api-docs-schema :model="resp.schema" :schemas="schemas"></api-docs-schema>
                  </div>
                </main>
              </b-tab-item>
              <!-- eslint-disable-next-line vue/no-use-v-if-with-v-for -->
              <b-tab-item label="Error" v-for="(resp, i) in path.responses" :key="i" v-if="isErrorResponse(resp.statusCode)">
                <main>
                  <p>{{resp.description}}</p>
                  <label class="label">{{resp.statusCode}} (text/plain)</label>
                </main>
              </b-tab-item>
            </b-tabs>
          </aside>
        </article>
      </div>
    </div>
  </div>
</template>
<script>
import { pinToDashMixin } from '../shared/mixins/pinToDash';
import ApiDocsSchema from './ApiDocsSchemaComponent.vue';
import TryItOut from './TryItOutComponent.vue';

export default {
  data() {
    return {
      openedDetails: [],
      terminalOpen: false
    }
  },
  mixins: [pinToDashMixin],
  async created() {
    await this.$store.dispatch('getApiDocsJson');
  },
  components: {
    ApiDocsSchema,
    TryItOut
  },
  computed: {
    appConfig() {
      return this.$store.state.appConfig;
    },
    docsJson() {
      return this.$store.state.api.docsJson;
    },
    paths() {
      const { paths } = this.$store.state.api;
      if (!paths) return []; // early return if null
      // 1. Instantiate return object
      const returnObject = [];
      // 2. Iterate over paths
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const pkey in paths) {
        // 2a. Get the verbs object
        const verbsObject = paths[pkey] // <- post, get;
        // 2b. Iterate over verbs
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const vkey in verbsObject) {
          const verbValue = verbsObject[vkey]; // <- tags/requestBody/responses
          const category = verbValue.tags.length > 0 ? verbValue.tags[0] : 'Uncategorized';
          // const desc = verbValue.description;
          const { description, summary } = verbValue;
          const responses = [];
          // eslint-disable-next-line no-restricted-syntax, guard-for-in
          for (const rkey in verbValue.responses) {
            const response = verbValue.responses[rkey];
            // create the base object
            const respRo = {
              statusCode: rkey,
              description: response.description
            }
            // if the response has content, add the keys
            if (response.content) {
              // eslint-disable-next-line prefer-destructuring
              respRo.contentType = Object.keys(response.content)[0];
              // get the response schema key
              const contentType = response.content[Object.keys(response.content)[0]];
              respRo.nullable = respRo.contentType.schema ? contentType.schema.nullable || false : false;
              // get the class schema
              if (contentType.schema.oneOf) { // single response
                if (contentType.schema.oneOf.length == 1) {
                  const classRef = contentType.schema.oneOf[0].$ref;
                  if (classRef) {
                    const splitArray = classRef.split('/');
                    const className = splitArray[splitArray.length - 1];                    
                    const schema = this.schemas[className];
                    respRo.schema = schema;
                  }
                }
              } else { // list response
                // eslint-disable-next-line no-lonely-if
                if (contentType.schema.$ref) {
                  const splitArray = contentType.schema.$ref.split('/');
                  const className = splitArray[splitArray.length - 1];
                  const schema = this.schemas[className];
                  respRo.schema = className == 'ProblemDetails' ? null : schema;
                } else if (contentType.schema.items) {
                  const splitArray = contentType.schema.items.$ref.split('/');
                  const className = splitArray[splitArray.length - 1];
                  const schema = this.schemas[className];
                  respRo.schema = schema;
                }
              }
            }

            // add response to list
            responses.push(respRo);
          }
          const parameters = [];
          if (verbValue.parameters) {
            verbValue.parameters.forEach(parameter => {
              if (parameter.name == 'version') return;
              parameters.push({
                name: parameter.name,
                required: parameter.required,
                description: parameter.description,
                in: parameter.in,
                type: parameter.schema.type,
                format: parameter.schema.format
              })
            });
          }
          // model if post or put
          let viewModel = null;
          if (verbValue.requestBody && verbValue.requestBody.content && verbValue.requestBody.content['application/json'] && verbValue.requestBody.content['application/json'].schema) {
            const schemaRef = verbValue.requestBody.content['application/json'].schema.$ref;
            if (schemaRef) {
              const splitArray = schemaRef.split('/');
              const className = splitArray[splitArray.length - 1];
              if (className) {
                viewModel = this.schemas[className];
              }
            }
          }

          // 2b
          const actualPath = pkey.replace('{version}', this.info.version);
          let shortcode = actualPath.slice(1).replace(/(\/)/gi, '_').replace('{', '-').replace('}', '-');
          shortcode = `${shortcode}_${vkey}`;
          const objectToReturn = {
            verb: vkey,
            path: actualPath,
            shortcode,
            cat: category,
            summary,
            responses,
            parameters,
            remarks: description,
            headers: [{ name: 'Authentication', description: 'Your bearer token.' }],
            requestUrl: `${this.info.url}${actualPath}`,
            model: viewModel
          };
          returnObject.push(objectToReturn);
        }
      }
      // 3. Return object
      return returnObject;
    },
    schemas() {
      return this.$store.state.api.schemas;
    },
    controllers() {
      const { paths } = this.$store.state.api;
      if (!paths) return []; // early return if null
      const returnObject = [];
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const pkey in paths) {
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const vkey in paths[pkey]) {
          const category = paths[pkey][vkey].tags.length > 0 
            ? paths[pkey][vkey].tags[0] 
            : 'Uncategorized';
          if (returnObject.indexOf(category) == -1) returnObject.push(category);
        }
      }
      return returnObject;
    },
    info() {
      return this.$store.state.api.info;
    }
  },
  methods: {
    getColorFromVerb(verb, type) {
      const prefix = type == 'tag' ? 'is-' : 'has-text-';

      switch (verb) {
        case 'get':
          return `${prefix}success`;
        case 'post':
          return `${prefix}info`;
        case 'put':
          return `${prefix}primary`;
        case 'delete':
          return `${prefix}danger`;
        default:
          return `${prefix}light`;
      }
    },
    isSuccessResponse(code) {
      return code >= 200 && code < 300;
    },
    isErrorResponse(code) {
      return code >= 300 && code < 500;
    },
    isActiveLink(hash) {
      return hash === this.$route.hash;
    },
    scrollTo(ref) {
      return ref;
    },
    buildModelPropertyArray(model) {
      const properties = [];
      // eslint-disable-next-line no-restricted-syntax, guard-for-in
      for (const key in model.properties) {
        const value = model.properties[key];
        properties.push({
          name: key,
          type: value.type,
          description: value.description,
          required: model.required && model.required.includes(key),
          nullable: value.nullable || false,
          restrictions: Object.keys(value).map(k => {
            if (k != 'type' && k != 'description' && k != 'nullable') {
              const newReturn = {};
              newReturn[k] = value[k];
              return newReturn;
            }
            return k;
          }).filter(v => (v))
        });
      }
      return properties;
    }
  },
  watch: {
    // eslint-disable-next-line quote-props, no-unused-vars
    '$route': function (newvalue, oldvalue) {
      if (newvalue) {
        const { hash } = newvalue;
        if (hash) {          
          const ref = hash.slice(1, hash.length);
          if (this.$refs[ref][0]) {
            this.$refs[ref][0].scrollIntoView();
          } else {
            this.$refs[ref].scrollIntoView();
          }
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../styles/_colors';

.kpt-api-root {
  position: relative;
  height: 100%;

  .kpt-api-content {
    position: relative;
    height: calc(100% - 50px);
    overflow-y: auto;
    display: flex;

    .kpt-api-listing {
      width: 300px;
      padding: 24px;
      background-color: $white-bis;
      position: fixed;
      overflow-y: auto;
      height: calc(100% - 98px);

      ul {
        margin-left: 12px;
        margin-bottom: 12px;

        li {
          a {
            color: $grey-darker;
          }
          &.is-active {
            a {
              color: $cyan;
            }
          }
        }
      }
    }

    .kpt-api-articles {
      flex: 1 0 auto;
      padding-left: 300px;
      width: calc(100% - 300px);

      article.kpt-api-endpoint {
        background: linear-gradient(to right,#fff 0,#fff 60%,#232323 60%);
        padding: 0 0 80px 0;
        display: flex;
        
        main.kpt-api-request {
          width: 60%;
          padding: 24px;
        }
        aside.kpt-api-models, aside.kpt-api-response {
          width: 40%;
          padding: 24px;

          .tabs.is-toggle li.is-active a {
            background-color: white;
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
@import '../styles/_colors';

aside.kpt-api-models, aside.kpt-api-response {
  color: $grey-light;

  label.label {
    margin: 5px 0;
    color: $grey-light;
  }

  .tabs.is-toggle {
    li {
      a {
        background-color: transparent;
        border: 1px solid transparent;
        color: $grey-light;

        &:hover {
          border: 1px solid $grey-dark;
          color: $white-bis;
        }
      }
      &.is-active {
        a {
          color: $cyan;
          border: 1px solid transparent;

          &:hover {
          color: $cyan;
          border: 1px solid transparent;
        }
        }
      }
    }
  }
  .kpt-api-viewmodel {
    margin-bottom: 4px; 
    border-bottom: 1px solid $grey-light; 
    padding-bottom: 4px;

    &:last-child {
      border-bottom: 0;
    }
  }
}

</style>
